import React from 'react';
import {
  MobileHeader, Skeleton, SkeletonAvatar, SkeletonButton, SkeletonRegion, SkeletonText,
  SkeletonTabs,
} from '@parsina/framework';
import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';

// The app-level skeleton vocabulary — DESIGN.md §"Loading & skeleton".
//
// The kit ships the atoms (`Skeleton`, `SkeletonText`, …) and the generic
// composites (`SkeletonTable`, `SkeletonList`, …). What it cannot know is this
// app's own chrome: the InnerPageHeader band with a patient avatar, the mobile
// gradient header, the StatTiles strip riding its edge, the odontogram box.
// Those live here, ONCE, and every screen's `X.skeleton.jsx` composes its page
// from them — so امروز and پرونده are different shapes, but the header band on
// پرونده and on جلسهٔ درمان is the same band.
//
// Geometry mirrors the real component it stands for, measured from the kit:
//   InnerPageHeader  min-height 4rem, padding 0.75rem 1.75rem, avatar 44
//   MobileHeader     the REAL component, with skeleton blocks for its text
//   StatTiles        4-up grid, gap 0.5rem, tile padding 0.625rem 0.25rem
//   Odontogram       svg viewBox 620×396, width 100%, max-width 760
// Change one of those and change its skeleton in the same commit.

/**
 * The root of a skeleton: announced once, busy, labelled, `data-state="loading"`.
 *
 * The kit's `SkeletonRegion` owns the whole behaviour — including the nesting
 * rule, that a region mounted inside another announced region becomes a plain
 * hidden box so assistive tech hears ONE "loading" (DESIGN.md §"Loading &
 * skeleton", A11y). All this wrapper adds is the app's translated default
 * label, which the kit cannot know.
 */
export const SkeletonPage = ({ label, ...rest }) => {
  const { t } = useTranslation();
  return <SkeletonRegion label={label ?? t('common.loading')} {...rest} />;
};

/**
 * The `InnerPageHeader` footprint: back affordance, optional 44px avatar, an
 * 18px title with a meta line (and `stats` pills), `actions` buttons at the
 * far edge. Used by پرونده, نسخه and جلسهٔ درمان on the desktop.
 */
export const InnerHeaderSkeleton = ({ avatar = true, meta = true, stats = 0, actions = 1 }) => (
  <div
    aria-hidden
    style={{
      display: 'flex', alignItems: 'center', gap: '1rem', minHeight: '4rem',
      paddingInline: '1.75rem', paddingBlock: '0.75rem',
      background: C.card, borderBottom: `1px solid ${C.border}`, flexShrink: 0,
    }}
  >
    <Skeleton width={72} height={36} radius={8} />
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
      {avatar && <SkeletonAvatar size={44} />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        <Skeleton width={180} height={18} />
        {meta && <Skeleton width={240} height={12} />}
      </div>
    </div>
    {stats > 0 && (
      <div style={{ display: 'flex', gap: '0.5625rem' }}>
        {Array.from({ length: stats }, (_, i) => (
          <Skeleton key={i} width={64} height={38} radius={10} />
        ))}
      </div>
    )}
    {actions > 0 && (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {Array.from({ length: actions }, (_, i) => <SkeletonButton key={i} width={i === 0 ? 112 : 96} />)}
      </div>
    )}
  </div>
);

/**
 * The mobile gradient band, drawn by the REAL `MobileHeader` so its padding,
 * safe-area inset and decorative circles are exactly the ones the data will
 * land in. Only the text is placeholder.
 *   back      content mode (a back affordance + a title line)
 *   greeting  cockpit mode with the avatar/greeting row
 *   subtitle  a second line under the title
 *   children  the context-widget row (a date navigator, a balance)
 *
 * Every block inside the band is `tone="onDark"`: the kit's neutral-grey sheen
 * is meant for a white surface and reads as a hole on navy.
 */
export const MobileHeaderSkeleton = ({ back = false, greeting = false, subtitle = true, children, ...rest }) => {
  const { t } = useTranslation();
  const title = (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Skeleton width={160} height={20} tone="onDark" />
      {subtitle && <Skeleton width={110} height={12} tone="onDark" />}
    </span>
  );
  return (
    <MobileHeader
      aria-hidden
      title={title}
      onBack={back ? () => {} : undefined}
      backLabel={t('common.actions.back')}
      user={greeting ? { name: ' ' } : undefined}
      greeting={greeting ? <Skeleton width={72} height={10} tone="onDark" /> : undefined}
      {...rest}
    >
      {children}
    </MobileHeader>
  );
};

/** The `StatTiles` strip: `count` tiles, 4-up by default, riding the header's edge. */
export const MobileTilesSkeleton = ({ count = 4, columns = count }) => (
  <div
    aria-hidden
    style={{
      display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: '0.5rem',
    }}
  >
    {Array.from({ length: count }, (_, i) => (
      <div
        key={i}
        style={{
          background: C.card, border: `1px solid ${C.border}`, borderRadius: '0.75rem',
          padding: '0.625rem 0.25rem', display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 6,
        }}
      >
        <Skeleton width="45%" height={17} />
        <Skeleton width="60%" height={9} />
      </div>
    ))}
  </div>
);

/** The `SectionNav` strip (mobile tab bar): icon over label, ≥44px, `count` items. */
export const SectionNavSkeleton = ({ count = 3 }) => (
  <div
    aria-hidden
    style={{
      display: 'flex', padding: '0 0.25rem', borderBottom: `1px solid ${C.border}`,
      background: C.card,
    }}
  >
    {Array.from({ length: count }, (_, i) => (
      <div
        key={i}
        style={{
          flex: 1, minHeight: 44, padding: '0.5rem 0.875rem', display: 'flex',
          flexDirection: 'column', alignItems: 'center', gap: 4,
        }}
      >
        <Skeleton width={16} height={16} radius={4} />
        <Skeleton width={48} height={10} />
      </div>
    ))}
  </div>
);

/** The desktop `SegmentedControl` strip: a pill group `count` wide. */
export const SegmentedSkeleton = ({ count = 3, height = 40 }) => (
  <div
    aria-hidden
    style={{
      display: 'inline-flex', gap: 4, padding: 4, borderRadius: 12,
      background: CT.surface2, border: `1px solid ${C.border}`,
    }}
  >
    {Array.from({ length: count }, (_, i) => (
      <Skeleton key={i} width={[88, 104, 76, 96, 84][i % 5]} height={height - 8} radius={8} />
    ))}
  </div>
);

/**
 * The odontogram box: the white card and, inside it, one block at the svg's
 * 620×396 aspect so the arch lands without moving anything below it.
 */
export const OdontogramSkeleton = ({ radius = 12 }) => (
  <div
    aria-hidden
    style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: radius, padding: '8px 4px 4px',
    }}
  >
    <Skeleton
      width="100%"
      height="auto"
      radius={10}
      style={{ aspectRatio: '620 / 396', maxWidth: 760, margin: '0 auto' }}
    />
  </div>
);

/**
 * A key/value row of `MobileInfoRow`'s size — the mobile record's demographic
 * card is a stack of these inside one Card.
 */
export const InfoRowsSkeleton = ({ count = 6 }) => (
  <div aria-hidden style={{ display: 'flex', flexDirection: 'column' }}>
    {Array.from({ length: count }, (_, i) => (
      <div
        key={i}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          minHeight: 44, borderTop: i > 0 ? `1px solid ${C.border}` : 'none',
        }}
      >
        <Skeleton width={`${28 + ((i * 7) % 18)}%`} height={11} />
        <Skeleton width={`${30 + ((i * 13) % 26)}%`} height={13} />
      </div>
    ))}
  </div>
);

/** A `Field` in the desktop record: an 11.5px label over a 13.5px value. */
export const FieldSkeleton = ({ valueWidth = '70%' }) => (
  <div aria-hidden style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
    <Skeleton width="45%" height={11} />
    <Skeleton width={valueWidth} height={14} />
  </div>
);

/** A titled inline row (`fontSize 13.5` title + trailing meta) — the desktop history lists. */
export const TitledRowsSkeleton = ({ count = 3, gap = 12 }) => (
  <div aria-hidden style={{ display: 'flex', flexDirection: 'column', gap }}>
    {Array.from({ length: count }, (_, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Skeleton width={`${34 + ((i * 11) % 22)}%`} height={14} />
        <Skeleton width={56} height={22} radius={999} />
        <Skeleton width={70} height={12} style={{ marginInlineStart: 'auto' }} />
      </div>
    ))}
  </div>
);

export { SkeletonText, SkeletonTabs };
